import { animate, style, transition, trigger } from '@angular/animations';

import { Component, ElementRef, input, output, signal, ViewChild } from '@angular/core';
import { CanvasElementComponent } from '@features/canvas/canvas-element/canvas-element.component';
import { CanvasScrollerComponent } from '@features/canvas/canvas-scroller/canvas-scroller.component';
import { ICanvasElement, IDragEvent } from '@features/canvas/canvas.types';
import { LpCanvasRendererArrowsComponent } from '@features/lp/lp-canvas/lp-canvas-renderer-arrows/lp-canvas-renderer-arrows.component';
import { EASE_IN_BACK, EASE_OUT_BACK } from '@shared/css.utils';
import { add, one, scale } from '@shared/vector.utils';
import { Vector2, Vector2CanvasNormalized } from '@typings/vectors';
import { Id } from '@typings/id';

@Component({
  selector: 'app-canvas-renderer',
  imports: [CanvasElementComponent, CanvasScrollerComponent, LpCanvasRendererArrowsComponent],
  templateUrl: './canvas-renderer.component.html',
  styleUrl: './canvas-renderer.component.css',
  animations: [
    trigger('scale', [
      transition(':enter', [style({ scale: 0 }), animate(`0.25s ${EASE_OUT_BACK}`, style({ scale: 1 }))]),
      transition(':leave', [style({ scale: 1 }), animate(`0.25s ${EASE_IN_BACK}`, style({ scale: 0 }))]),
    ]),
  ],
})
export class CanvasRendererComponent {
  elements = input.required<ICanvasElement[]>();
  overrides = input<Record<Id, Partial<{ position: Vector2CanvasNormalized; zIndex: number }>>>();
  size = input<Vector2CanvasNormalized>(one() as Vector2CanvasNormalized);
  clicked = output<Id>();
  grabbed = output<IDragEvent>();
  dragged = output<IDragEvent>();
  dropped = output<IDragEvent>();
  origin = signal([0, 0] as Vector2CanvasNormalized);
  screenSize = signal([0, 0] as Vector2);
  disablePointerEvents = signal(false);

  @ViewChild('canvas', { read: ElementRef })
  canvasEl!: ElementRef<HTMLElement>;

  calculateElementPosition(element: ICanvasElement): Vector2CanvasNormalized {
    return add(element.position, scale(scale(this.origin(), -1), element.speed ?? 1)) as Vector2CanvasNormalized;
  }

  handleDragStateChanged(state: 'START' | 'STOP') {
    this.disablePointerEvents.set(state === 'START');
  }

  handlePanned(origin: Vector2) {
    const normalized = this.getNormalizedPositionFromPixels(origin[0], origin[1]);
    this.origin.set(normalized);
  }

  handleGrab(event: PointerEvent, id: Id) {
    const position = this.getNormalizedCanvasPositionFromPointerEvent(event);
    this.grabbed.emit({ id, position });
  }

  handleDrag(event: PointerEvent, id: Id) {
    const position = this.getNormalizedCanvasPositionFromPointerEvent(event);
    this.dragged.emit({ id, position });
  }

  handleDrop(event: PointerEvent, id: Id) {
    const position = this.getNormalizedCanvasPositionFromPointerEvent(event);
    this.dropped.emit({ id, position });
  }

  private getNormalizedPositionFromPixels(x: number, y: number): Vector2CanvasNormalized {
    const canvasEl = this.canvasEl.nativeElement;
    const rect = canvasEl.getBoundingClientRect();
    return [x / rect.width, y / rect.height] as Vector2CanvasNormalized;
  }

  private getNormalizedCanvasPositionFromPointerEvent(event: PointerEvent): Vector2CanvasNormalized {
    const canvasEl = this.canvasEl.nativeElement;
    const rect = canvasEl.getBoundingClientRect();
    return [
      this.origin()[0] + (event.pageX - rect.left) / rect.width,
      this.origin()[1] + (event.pageY - rect.top) / rect.height,
    ] as Vector2CanvasNormalized;
  }
}
